import { useEffect, useMemo, useRef } from "react";
import { X, SlidersHorizontal } from "lucide-react";
import { useMobileOverlayData } from "@/contexts/MobileOverlayContext";
import SearchBar from "@/components/SearchBar";
import FilterField from "@/components/ui/FilterField";
import { MultiSelect } from "@/components/ui/MultiSelect";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function MobileFiltersDrawer({ open, onClose }: Props): React.JSX.Element {
  const { filterProps } = useMobileOverlayData();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Foco automático no primeiro campo ao abrir (accessibility).
  useEffect(() => {
    if (!open || !drawerRef.current) return;
    const el = drawerRef.current.querySelector<HTMLElement>(
      'input, select, button, [tabindex]:not([tabindex="-1"])',
    );
    el?.focus();
  }, [open]);

  const fp = filterProps;

  const attributeFilters = useMemo(() => {
    if (!fp) return null;
    return fp.attributeCategories.map((cat) => {
      const selected = fp.selectedAttributes[cat.id] || [];
      return (
        <FilterField
          key={cat.id}
          label={cat.name}
          className="sm:w-auto sm:min-w-[160px]"
        >
          <MultiSelect
            options={cat.options}
            selected={selected}
            onChange={(vals) =>
              fp.setSelectedAttributes((p) => ({ ...p, [cat.id]: vals }))
            }
            placeholder="Todos"
            className="!border-none !bg-transparent !p-0 !shadow-none !min-h-0 !h-auto"
          />
        </FilterField>
      );
    });
  }, [fp]);

  const actionButton = useMemo(() => {
    if (!fp) return null;
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            fp.clearFilters();
          }}
          disabled={!fp.hasActiveFilters}
          className={[
            "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-bold transition-all",
            fp.hasActiveFilters
              ? "border-zinc-300 text-ink-700 hover:bg-cream-100"
              : "border-zinc-200 text-zinc-400 cursor-not-allowed",
          ].join(" ")}
        >
          <X className="w-4 h-4" /> Limpar
        </button>
        <button
          type="button"
          onClick={() => {
            fp.onApply();
            onClose();
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-heat-grad text-white text-xs font-bold shadow-heat-glow hover:scale-[1.02] transition-transform"
        >
          Ver resultados
        </button>
      </div>
    );
  }, [fp, onClose]);

  if (!fp) {
    return (
      <div
        data-testid="mobile-filters-drawer"
        className="md:hidden fixed inset-0 z-[60] hidden"
        aria-hidden
      />
    );
  }

  return (
    <div
      data-testid="mobile-filters-drawer"
      className={`md:hidden fixed inset-0 z-[60] transition-opacity duration-200 ${
        open ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`absolute left-0 top-0 bottom-0 w-[88%] max-w-sm bg-cream-50 border-r border-cream-200 shadow-2xl flex flex-col transition-transform duration-250 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Filtros de busca"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-cream-200 bg-white">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-heat-600" />
            <span className="font-display font-bold text-base text-ink-900">
              Filtros
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] w-11 h-11 inline-flex items-center justify-center rounded-full hover:bg-cream-100 text-ink-600 transition"
            aria-label="Fechar filtros"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — scrollável */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <SearchBar
            variant="inline"
            hideSearchInput={false}
            q={fp.q}
            onQChange={fp.onQChange}
            category={fp.category}
            categories={fp.categories}
            onCategoryChange={fp.onCategoryChange}
            subcategory={fp.subcategory}
            subcategories={fp.subcategories}
            onSubcategoryChange={fp.onSubcategoryChange}
            location={fp.location}
            onLocationChange={fp.onLocationChange}
            attributeFilters={attributeFilters}
            actionButton={null}
            onlineOnly={fp.onlineOnly}
            onOnlineToggle={() => fp.setOnlineOnly(!fp.onlineOnly)}
            sortMode={fp.sortMode}
            onSortChange={fp.setSortMode}
            className="shadow-none border-0 bg-transparent p-0"
          />

          {/* Serviços */}
          <div className="mt-4">
            <div className="flex items-center gap-2 w-full mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-heat-700">
                Serviços
              </span>
              <span className="flex-1 h-px bg-cream-200" />
            </div>
            <div className="flex flex-wrap gap-2">
              {fp.serviceOptions.map((svc) => {
                const selected = fp.selectedService.includes(svc.name);
                return (
                  <button
                    key={svc.id}
                    type="button"
                    onClick={() => {
                      const next = selected
                        ? fp.selectedService.filter((s) => s !== svc.name)
                        : [...fp.selectedService, svc.name];
                      fp.setSelectedService(next);
                    }}
                    className={[
                      "text-xs font-bold px-3 py-1.5 rounded-full border transition-all",
                      selected
                        ? "bg-heat-grad text-white border-transparent shadow-heat-glow"
                        : "bg-white text-ink-700 border-cream-200 hover:border-heat-300",
                    ].join(" ")}
                  >
                    {svc.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-cream-200 bg-white flex items-center gap-2">
          {actionButton}
        </div>
      </div>
    </div>
  );
}